import { ArrowRight, Heart, Plus } from '@phosphor-icons/react';
import {
  Agenda,
  Celebration,
  Heading,
  Icon,
  Surface,
  Text,
  Sheet,
  WeatherCard,
  WeatherDetails,
  WeekList,
  buttonStyles,
  cn,
} from '@shh/ui';
import { useState } from 'react';
import { ProgressBar } from 'react-aria-components';
import { Link } from 'react-router';
import { useLens } from '../app/lens';
import { shows } from '../app/view';
import { Approvals } from '../components/Approvals';
import { EventSheet } from '../components/EventSheet';
import { Hints } from '../components/Hints';
import { FestiveBand, FestivePopup } from '../components/Festive';
import { InstallPrompt, useShowInstall } from '../components/InstallPrompt';
import { TrialEndingNote } from '../components/Plus';
import { PushPrompt } from '../components/PushPrompt';
import { WallReminder, useUpcoming, useWallGong } from '../components/WallReminder';
import { Pinboard } from '../components/Pinboard';
import { ShoppingCard } from '../components/ShoppingCard';
import { TodayMeal } from '../components/TodayMeal';
import { UpdateHint } from '../components/WhatsNew';
import { ForEveryone, PersonTasks, useTick } from '../components/tasks';
import { agendaItems, eventStates, relative, weekDays } from '../data/agenda';
import { calendarDay } from '../data/calendar';
import { useClock } from '../data/clock';
import { currentDaypart, involves, memberById, type E, type M } from '../data/derive';
import { useFamily } from '../data/family';
import { hintsFor } from '../data/hints';
import { useWeather } from '../data/weather';
import { cardColumns } from '../lib/display';
import { dateFormat, numberFormat } from '../lib/locale';
import { FamilyModeOffer } from '../components/FamilyModeOffer';
import { todayText } from '../i18n/today';
import { calendarText } from '../i18n/calendar';

const dateFmt = (tz: string) =>
  dateFormat({
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: tz,
  });
const numFmt = numberFormat();

function greeting(time: string) {
  const h = Number(time.slice(0, 2));
  if (h < 11) return todayText.morning;
  if (h < 17) return todayText.hello;
  if (h < 22) return todayText.evening;
  return todayText.night;
}

/**
 * The dashboard – the family's hub. Everybody sees what's coming (shared plan + next up),
 * everybody sees their tasks and progress, and the family goal shows that it all adds up.
 */
/** For the celebration: the routine that was just finished. */
const ROUTINE = todayText.routines;

export function TodayPage() {
  const f = useFamily();
  const lens = useLens();
  const showInstall = useShowInstall();
  const [installDone, setInstallDone] = useState(false);
  const now = useClock(f.timeZone);
  const weather = useWeather(f.household?.location, f.timeZone);
  // with the day it was tapped on: series and the week list open events on other days
  const [openEvent, setOpenEvent] = useState<{ id: string; date: string } | null>(null);
  const [celebrate, setCelebrate] = useState<M | null>(null);
  const [weatherOpen, setWeatherOpen] = useState(false);
  const { tick, together, picker } = useTick(setCelebrate);
  const daypart = currentDaypart(now);
  const soon = useUpcoming(now);
  useWallGong(soon, lens.isWall);

  // "Als Nächstes" for the lens person (or anybody) plus the two after it.
  const today = calendarDay(f, f.today);
  const states = eventStates(today, now);
  const relevant = today.filter((e) => e.start && (lens.id === 'all' || involves(e, lens.id)));
  const upcoming = relevant.filter((e) => ['next', 'later'].includes(states.get(e.id) ?? ''));
  const next: E | undefined = upcoming[0];

  const people = f.members.filter((m) => shows(lens.id, m.id));
  const ordered = [...people].sort(
    (a, b) => Number(a.role === 'parent') - Number(b.role === 'parent'),
  );
  const latestKudos = f.kudos[0];

  return (
    <>
      {/* 1.29: the family mode where it fits – parents on their own device, never the wall */}
      {lens.canParent && !lens.isWall && <FamilyModeOffer />}
      {/* once after an update – never on the shared wall screen */}
      {!lens.isWall && <UpdateHint />}
      {/* one card at a time: first the home screen, then reminders */}
      {!lens.isWall &&
        (showInstall && !installDone ? (
          <InstallPrompt onDone={() => setInstallDone(true)} />
        ) : (
          <PushPrompt />
        ))}
      <FestivePopup />
      {/* Larovi Plus (1.21): calm note in the trial's last days – parents only, never the wall */}
      {lens.canParent && !lens.isWall && <TrialEndingNote />}
      {/* 1 – orientation: one slim row, so the day plan starts high up */}
      <div className="flex flex-col gap-gap-sm">
        <div className="flex items-end justify-between gap-gap-md">
          <div className="flex min-w-0 flex-col">
            <span className="text-sm text-text-secondary">
              {dateFmt(f.timeZone).format(new Date())}
            </span>
            <Heading level={2} size="lg">
              {todayText.greeting(greeting(now), lens.member ? lens.member.name : todayText.family)}
            </Heading>
          </div>
          <span className="font-display text-3xl font-black">{now}</span>
        </div>
        {weather ? (
          <WeatherCard
            variant="strip"
            place={f.household?.location?.name ?? ''}
            temp={weather.temp}
            icon={weather.icon}
            summary={weather.summary}
            hint={weather.hint}
            hours={weather.hours}
            onOpen={() => setWeatherOpen(true)}
          />
        ) : f.household?.location ? null : (
          // without a home there is no weather and the sky only follows the time zone
          lens.canParent && (
            <p className="flex flex-wrap items-center gap-x-gap-sm text-sm text-text-secondary">
              <span>{todayText.noHome}</span>
              <Link
                to="/einstellungen#familie"
                className={cn(buttonStyles({ variant: 'quiet' }), 'text-sm')}
              >
                {todayText.addHome} <Icon icon={ArrowRight} size="sm" weight="bold" />
              </Link>
            </p>
          )
        )}
        <FestiveBand />
        {/* the wall: 15 minutes before, the next event as a big reminder card */}
        {lens.isWall && soon && <WallReminder upcoming={soon} />}
        {/* notes are for everybody and short – right under the weather, above the day plan */}
        <Pinboard />
        {/* "denkt mit": what to pack, rain during football, tomorrow's special events */}
        <Hints hints={hintsFor(f, now, weather)} />
      </div>

      {/*
        2 – on wide screens two independent columns: plan + coming days left, tasks + together
        right. The tasks span two rows and the second row takes the extra height (1fr), so a long
        task list never pushes "Die nächsten Tage" down. DOM order = phone order for screen readers.
      */}
      <div className="grid grid-cols-1 gap-section xl:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] xl:grid-rows-[auto_1fr] xl:items-start">
        <section
          aria-labelledby="plan"
          className="flex flex-col gap-gap-md xl:col-start-1 xl:row-start-1"
        >
          <div className="flex items-baseline justify-between gap-gap-md">
            <Heading level={2} size="md" id="plan">
              {todayText.dayPlan}
            </Heading>
            <Link
              to="/kalender?ansicht=tag"
              className={cn(buttonStyles({ variant: 'quiet' }), 'text-sm')}
            >
              {todayText.calendar} <Icon icon={ArrowRight} size="sm" weight="bold" />
            </Link>
          </div>
          {today.length ? (
            // one day plan for Heute and the calendar's day (Mike, 18.10.)
            <Agenda
              items={agendaItems(
                f,
                f.today,
                now,
                lens.id,
                (e) =>
                  e.bring?.length ? <span>{calendarText.bring(e.bring.join(', '))}</span> : null,
                { list: calendarDay(f, f.today) },
              ).map((i) => (i.id === next?.id ? { ...i, when: relative(next, now) } : i))}
              nowLabel={now}
              pinAllDay
              onItemPress={(id) => setOpenEvent({ id, date: f.today })}
            />
          ) : (
            <Surface tone="sunken" className="flex flex-col gap-gap-sm">
              <Text>{todayText.nothingToday}</Text>
              <Text size="sm" tone="muted">
                {todayText.nothingTodayHint}
              </Text>
            </Surface>
          )}
        </section>

        <section
          aria-labelledby="todo"
          className="@container flex flex-col gap-gap-lg xl:col-start-2 xl:row-span-2 xl:row-start-1"
        >
          {lens.canParent && <Approvals hideWhenEmpty />}
          <div className="flex items-baseline justify-between gap-gap-md">
            <Heading level={2} size="md" id="todo">
              {todayText.tasksToday}
            </Heading>
            <Link to="/aufgaben" className={cn(buttonStyles({ variant: 'quiet' }), 'text-sm')}>
              {todayText.allTasks} <Icon icon={ArrowRight} size="sm" weight="bold" />
            </Link>
          </div>
          {/* stacked columns (like a pinboard): every card only as tall as its content */}
          <div className="columns-cards gap-gap-md" style={cardColumns(ordered.length)}>
            {ordered.map((m) => (
              <div key={m.id} className="mb-gap-md break-inside-avoid">
                <PersonTasks
                  member={m}
                  daypart="all"
                  focusNow
                  onTick={tick}
                  onTogether={together}
                  emphasis={lens.id === m.id ? 'focus' : 'normal'}
                  maxItems={lens.id === m.id ? 10 : 4}
                />
              </div>
            ))}
          </div>
          <ForEveryone onTick={tick} onTogether={together} />
          <TodayMeal />
          <ShoppingCard />
        </section>

        <section
          aria-labelledby="week"
          className="flex flex-col gap-gap-md xl:col-start-1 xl:row-start-2"
        >
          <Heading level={2} size="md" id="week">
            {todayText.nextDays}
          </Heading>
          <Surface padding="sm">
            <WeekList
              days={weekDays(f, 1, 6, lens.id)}
              onItemPress={(id, date) => setOpenEvent({ id, date })}
            />
          </Surface>
        </section>

        <section
          aria-labelledby="family"
          className="flex flex-col gap-gap-md xl:col-start-2 xl:row-start-3"
        >
          <Heading level={2} size="md" id="family">
            {todayText.together}
          </Heading>
          {f.goal ? (
            <Surface className="flex flex-col gap-gap-sm">
              <ProgressBar
                value={Math.min(f.goal.progress, f.goal.target)}
                maxValue={f.goal.target}
                aria-label={todayText.goalLabel(f.goal.title)}
                valueLabel={todayText.goalValue(
                  numFmt.format(f.goal.progress),
                  numFmt.format(f.goal.target),
                )}
                className="flex flex-col gap-gap-sm"
              >
                {({ percentage }) => (
                  <>
                    <div className="flex flex-wrap items-baseline justify-between gap-gap-sm">
                      <span className="font-display text-xl font-black">
                        {todayText.goalTitle(f.goal!.title)}
                      </span>
                      <span className="font-bold tabular-nums">
                        {numFmt.format(f.goal!.progress)} / {numFmt.format(f.goal!.target)}
                      </span>
                    </div>
                    <span className="h-4 overflow-hidden rounded-chip bg-reward-points-surface">
                      <span
                        className="block h-full rounded-chip bg-reward-points transition-all duration-(--shh-motion-duration-slow) ease-bounce"
                        style={{ width: `${percentage ?? 0}%` }}
                      />
                    </span>
                    <Text size="sm" tone="secondary">
                      {f.goal!.progress >= f.goal!.target
                        ? todayText.goalReached
                        : todayText.goalLeft(numFmt.format(f.goal!.target - f.goal!.progress))}
                    </Text>
                  </>
                )}
              </ProgressBar>
            </Surface>
          ) : null}
          {latestKudos ? (
            <Surface className="flex items-center gap-gap-md">
              <Icon icon={Heart} size="lg" weight="fill" className="text-reward-kudos" />
              <p>
                <strong>{memberById(f, latestKudos.memberId)?.name}</strong> {latestKudos.text}
                <span className="text-text-muted"> · {todayText.kudos}</span>
              </p>
            </Surface>
          ) : (
            lens.canParent && (
              <Text size="sm" tone="muted" className="flex items-center gap-gap-xs">
                <Icon icon={Plus} size="sm" /> {todayText.kudosHint}
              </Text>
            )
          )}
        </section>
      </div>

      <EventSheet
        eventId={openEvent?.id ?? null}
        date={openEvent?.date ?? f.today}
        onClose={() => setOpenEvent(null)}
      />
      {weather && (
        <Sheet
          isOpen={weatherOpen}
          onOpenChange={setWeatherOpen}
          title={todayText.weatherIn(f.household?.location?.name ?? todayText.yourPlace)}
          size="wide"
        >
          <WeatherDetails {...weather} hours={weather.next24} />
        </Sheet>
      )}
      {picker}
      <Celebration
        show={!!celebrate}
        title={celebrate ? todayText.routineDone(celebrate.name, ROUTINE[daypart]) : ''}
        subtitle={todayText.wellDone}
        onDone={() => setCelebrate(null)}
      />
    </>
  );
}
